import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateDraftApplicationDto,
  CreateDraftApplicationResponseDto,
} from "@workspace/dto/application/create-draft-application";
import type { FavouriteJobResponseDto } from "@workspace/dto/job/favourite-job";
import type { GetApplicationStatusResponseDto } from "@workspace/dto/job/get-application-status";
import type { GetFavouriteStatusResponseDto } from "@workspace/dto/job/get-favourite-status";
import type { UnfavouriteJobResponseDto } from "@workspace/dto/job/unfavourite-job";
import {
  Avatar,
  AvatarImage,
  InitialsAvatarFallback,
} from "@workspace/ui/components/avatar";
import { Button } from "@workspace/ui/components/button";
import { Card, CardContent } from "@workspace/ui/components/card";
import { cn } from "@workspace/ui/lib/utils";
import type { AxiosResponse } from "axios";
import { formatDistance } from "date-fns";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeft,
  Banknote,
  Building2,
  CircleCheck,
  Clock,
  MapPin,
} from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useSession } from "next-auth/react";
import type { MouseEvent, ReactNode } from "react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import type { ExtendedUser } from "@/auth";
import { Icons } from "@/components/icons";
import type { ApplicationListing } from "@/components/listings/types";
import { MdxContent } from "@/components/markdown/mdx-content";
import { LoginModal } from "@/components/modals/auth/login";
import { ShareJobPopover } from "@/components/share-job-popover";
import { COMMITMENTS, WORK_MODELS } from "@/config/constants";
import { getApiErrorMessage, getApiErrorType } from "@/lib/api-error";
import { axios } from "@/lib/axios";
import { formatCompensation } from "@/lib/compensation";
import { useTrackJobImpression } from "@/lib/hooks/use-track-job-impression";
import { toastMutationNetworkError } from "@/lib/query-failure";
import { queryKeys } from "@/lib/query-keys";
import { buildListingUrl } from "@/lib/slugify";
import { ListingSidebar } from "./listing-sidebar";

type BodySection = { key: string; label: string; content: string };

function MetadataChip({
  icon: Icon,
  children,
}: {
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1.5 rounded-md bg-muted px-2.5 py-1.5 font-medium text-foreground text-xs">
      <Icon className="size-3.5 text-muted-foreground" />
      {children}
    </div>
  );
}

function RelativeTime({ date }: { date: Date }) {
  return (
    <span suppressHydrationWarning>
      {formatDistance(date, new Date(), { addSuffix: true })}
    </span>
  );
}

function useListingHero(listing: ApplicationListing) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data: session, status } = useSession();
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  const favouriteStatusKey = queryKeys.favourites.status(listing.id);
  const { data: favouriteStatus } = useQuery({
    queryKey: favouriteStatusKey,
    queryFn: async () => {
      const response = await axios.get<
        GetFavouriteStatusResponseDto,
        AxiosResponse<GetFavouriteStatusResponseDto>
      >(`/jobs/${listing.id}/get-favourite-status`);
      return response.data;
    },
    enabled: status === "authenticated",
  });

  const applicationStatusKey = queryKeys.applications.status(listing.id);
  const { data: applicationStatus } = useQuery({
    queryKey: applicationStatusKey,
    queryFn: async () => {
      const response = await axios.get<
        GetApplicationStatusResponseDto,
        AxiosResponse<GetApplicationStatusResponseDto>
      >(`/jobs/${listing.id}/get-application-status`);
      return response.data;
    },
    enabled:
      status === "authenticated" &&
      (session?.user as ExtendedUser)?.type === "employee",
  });

  const trackImpression = useTrackJobImpression("direct_visit");

  useEffect(() => {
    trackImpression(
      listing.id,
      listing.organizationId,
      listing.storeId ?? undefined
    );
  }, [listing.id, listing.organizationId, listing.storeId, trackImpression]);

  const createDraftMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post<
        CreateDraftApplicationResponseDto,
        AxiosResponse<CreateDraftApplicationResponseDto>,
        CreateDraftApplicationDto
      >("/applications/create-draft-application", {
        job: listing.id,
      });
      return response.data;
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.applications.all,
      });
      await router.push(`/applications/${data.application.id}`);
    },
    onError: (error: unknown) => {
      if (toastMutationNetworkError(error, "Couldn't start application")) {
        return;
      }

      const type = getApiErrorType(error);
      const message = getApiErrorMessage(error);

      if (type === "APPLICATION_ALREADY_EXISTS") {
        toast.error("Already applied", {
          description:
            message ??
            "You have already applied to this job. Check your applications.",
        });
      } else if (type === "JOB_NOT_FOUND") {
        toast.error("Job unavailable", {
          description:
            message ?? "This job is no longer accepting applications.",
        });
      } else if (type === "APPLICATION_TO_YOUR_OWN_JOB_NOT_ALLOWED") {
        toast.error("Not allowed", {
          description: message ?? "You can't apply to your own job posting.",
        });
      } else {
        toast.error("Uh oh! Something went wrong.", {
          description: "Failed to start application. Please try again.",
        });
      }
    },
  });

  const handleStart = () => {
    if (createDraftMutation.isPending || status === "loading") {
      return;
    }

    if (status === "unauthenticated") {
      setLoginModalOpen(true);
      return;
    }

    if ((session?.user as ExtendedUser)?.type === "employer") {
      toast.error("Uh oh! Something went wrong.", {
        description: "Please switch to an employee account to apply.",
      });
      return;
    }

    if (applicationStatus?.hasApplied && applicationStatus?.applicationId) {
      router.push(`/applications/${applicationStatus.applicationId}`);
      return;
    }

    createDraftMutation.mutate();
  };

  const isFavourited = favouriteStatus?.isFavourited ?? false;

  const favouriteMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post<
        FavouriteJobResponseDto,
        AxiosResponse<FavouriteJobResponseDto>
      >(`/jobs/${listing.id}/favourite-job`);
      return response.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: favouriteStatusKey });
      const previous =
        queryClient.getQueryData<GetFavouriteStatusResponseDto>(
          favouriteStatusKey
        );
      queryClient.setQueryData<GetFavouriteStatusResponseDto>(
        favouriteStatusKey,
        { isFavourited: true }
      );
      return { previous };
    },
    onSuccess: () => {
      toast.success("Success.", {
        description: "Job added to favourites",
      });
    },
    onError: (error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(favouriteStatusKey, context.previous);
      }
      if (toastMutationNetworkError(error, "Couldn't update favourites")) {
        return;
      }
      const type = getApiErrorType(error);
      if (type === "FAVOURITE_ALREADY_EXISTS") {
        toast(getApiErrorMessage(error) ?? "Already saved");
      } else {
        toast.error("Uh Oh! Something went wrong.", {
          description:
            getApiErrorMessage(error) ?? "Failed to add to favourites",
        });
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.marketplace.nearby(),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.favourites.all }),
      ]);
    },
  });

  const unfavouriteMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post<
        UnfavouriteJobResponseDto,
        AxiosResponse<UnfavouriteJobResponseDto>
      >(`/jobs/${listing.id}/unfavourite-job`);
      return response.data;
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: favouriteStatusKey });
      const previous =
        queryClient.getQueryData<GetFavouriteStatusResponseDto>(
          favouriteStatusKey
        );
      queryClient.setQueryData<GetFavouriteStatusResponseDto>(
        favouriteStatusKey,
        { isFavourited: false }
      );
      return { previous };
    },
    onSuccess: () => {
      toast.success("Success.", {
        description: "Job removed from favourites",
      });
    },
    onError: (error, _variables, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(favouriteStatusKey, context.previous);
      }
      if (toastMutationNetworkError(error, "Couldn't update favourites")) {
        return;
      }
      const type = getApiErrorType(error);
      if (type === "FAVOURITE_NOT_FOUND") {
        toast(getApiErrorMessage(error) ?? "Already removed");
      } else {
        toast.error("Uh Oh! Something went wrong.", {
          description:
            getApiErrorMessage(error) ?? "Failed to remove from favourites",
        });
      }
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.marketplace.nearby(),
        }),
        queryClient.invalidateQueries({ queryKey: queryKeys.favourites.all }),
      ]);
    },
  });

  const toggleFavourite = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (status === "unauthenticated") {
      setLoginModalOpen(true);
      return;
    }

    if (isFavourited) {
      unfavouriteMutation.mutate();
    } else {
      favouriteMutation.mutate();
    }
  };

  const applyButtonLabel = (() => {
    if (applicationStatus?.hasApplied) {
      return applicationStatus.applicationStatus === "draft"
        ? "Continue Application"
        : "View application";
    }
    return "Apply now";
  })();

  const isStartingApplication =
    createDraftMutation.isPending || status === "loading";
  const isFavouriteButtonPending = status === "loading";

  const commitmentLabel =
    COMMITMENTS.find((c) => c.value === listing.commitment)?.label ?? "";
  const workModelLabel =
    WORK_MODELS.find((w) => w.value === listing.work_model)?.label ?? "";
  const posterLine = (() => {
    if (listing.organizationName && listing.storeName) {
      return `${listing.organizationName} · ${listing.storeName}`;
    }
    return listing.postedBy ?? listing.organizationName ?? "";
  })();
  const locationParts = [listing.address.city, listing.address.region].filter(
    Boolean
  );
  const locationText =
    locationParts.length > 0
      ? locationParts.join(", ")
      : (listing.address.street ?? "");

  const bodySections = [
    {
      key: "responsibilities",
      label: "What you\u2019ll do",
      content: listing.responsibilities,
    },
    { key: "benefits", label: "Benefits", content: listing.benefits },
  ].filter((s): s is BodySection => !!s.content);

  const userType: "employee" | "employer" | "anonymous" =
    status === "authenticated"
      ? ((session?.user as ExtendedUser)?.type ?? "anonymous")
      : "anonymous";

  const listingReturnTo = buildListingUrl(
    listing.slug,
    listing.title,
    listing.postedBy ?? ""
  );

  return {
    loginModalOpen,
    setLoginModalOpen,
    applyButtonLabel,
    isFavourited,
    isStartingApplication,
    isFavouriteButtonPending,
    handleStart,
    toggleFavourite,
    commitmentLabel,
    workModelLabel,
    locationText,
    posterLine,
    bodySections,
    userType,
    listingReturnTo,
  };
}

function HeroHeaderCard({
  listing,
  locationText,
  posterLine,
  commitmentLabel,
  workModelLabel,
  applyButtonLabel,
  isStartingApplication,
  isFavouriteButtonPending,
  isFavourited,
  userType,
  onApply,
  onToggleFavourite,
}: {
  listing: ApplicationListing;
  locationText: string;
  posterLine: string;
  commitmentLabel: string;
  workModelLabel: string;
  applyButtonLabel: string;
  isStartingApplication: boolean;
  isFavouriteButtonPending: boolean;
  isFavourited: boolean;
  userType: "employee" | "employer" | "anonymous";
  onApply: () => void;
  onToggleFavourite: (e: MouseEvent<HTMLButtonElement>) => void;
}) {
  return (
    <Card className="gap-0 rounded-lg py-0">
      <CardContent className="p-4">
        <div className="flex items-start gap-3.5">
          <Avatar className="size-11 shrink-0 rounded-full">
            <AvatarImage
              alt={listing.postedBy ?? ""}
              src={listing.postedByImage ?? undefined}
            />
            <InitialsAvatarFallback name={listing.postedBy ?? ""} />
          </Avatar>
          <div className="min-w-0 flex-1">
            <p className="font-medium text-muted-foreground text-xs">
              {posterLine}
              {locationText ? ` · ${locationText}` : ""}
            </p>
            <h1 className="font-semibold text-foreground text-xl tracking-tight">
              {listing.title}
            </h1>
            <p className="text-muted-foreground/70 text-xs">
              Posted <RelativeTime date={listing.createdAt} />
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <ShareJobPopover
              jobId={listing.id}
              organizationId={listing.organizationId}
              orgName={listing.postedBy ?? "unknown"}
              slug={listing.slug}
              source="listing_page"
              title={listing.title}
              userType={userType}
            />
            <Button
              className="size-8"
              disabled={isFavouriteButtonPending}
              onClick={onToggleFavourite}
              size="icon"
              variant="outline"
            >
              {isFavouriteButtonPending ? (
                <Icons.spinner className="size-3.5 animate-spin text-foreground transition" />
              ) : (
                <Icons.heart
                  className={cn(
                    "size-3.5 transition",
                    isFavourited
                      ? "fill-destructive text-destructive"
                      : "fill-transparent text-foreground"
                  )}
                />
              )}
            </Button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          <MetadataChip icon={Banknote}>
            {formatCompensation(listing.compensation)}
          </MetadataChip>
          {locationText ? (
            <MetadataChip icon={MapPin}>{locationText}</MetadataChip>
          ) : null}
          {commitmentLabel ? (
            <MetadataChip icon={Clock}>{commitmentLabel}</MetadataChip>
          ) : null}
          {workModelLabel ? (
            <MetadataChip icon={Building2}>{workModelLabel}</MetadataChip>
          ) : null}
        </div>

        <div className="mt-4 lg:hidden">
          <Button
            className="w-full"
            disabled={isStartingApplication}
            onClick={onApply}
            type="button"
          >
            {isStartingApplication ? (
              <Icons.spinner className="size-4 animate-spin" />
            ) : null}
            {isStartingApplication ? "Starting..." : applyButtonLabel}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function ListingBody({
  listing,
  bodySections,
}: {
  listing: ApplicationListing;
  bodySections: BodySection[];
}) {
  return (
    <>
      {listing.requirements ? (
        <div className="grid gap-3 md:grid-cols-[1.4fr_1fr]">
          <Card className="gap-0 rounded-lg py-0">
            <CardContent className="p-4">
              <h2 className="mb-2 font-semibold text-foreground text-sm">
                About the role
              </h2>
              <div className="text-secondary-foreground text-sm leading-relaxed">
                <MdxContent>{listing.description}</MdxContent>
              </div>
            </CardContent>
          </Card>
          <Card className="gap-0 rounded-lg py-0">
            <CardContent className="p-4">
              <h2 className="mb-2 font-semibold text-foreground text-sm">
                What you&apos;ll need
              </h2>
              <MdxContent
                components={{
                  ul: ({ children }: { children: ReactNode }) => (
                    <ul className="grid gap-1.5">{children}</ul>
                  ),
                  ol: ({ children }: { children: ReactNode }) => (
                    <ol className="grid gap-1.5">{children}</ol>
                  ),
                  li: ({ children }: { children: ReactNode }) => (
                    <li className="flex items-start gap-2 text-secondary-foreground text-sm">
                      <CircleCheck className="mt-0.5 size-4 shrink-0 text-foreground" />
                      <span>{children}</span>
                    </li>
                  ),
                  p: ({ children }: { children: ReactNode }) => (
                    <p className="text-secondary-foreground text-sm leading-relaxed">
                      {children}
                    </p>
                  ),
                }}
              >
                {listing.requirements}
              </MdxContent>
            </CardContent>
          </Card>
        </div>
      ) : (
        <Card className="gap-0 rounded-lg py-0">
          <CardContent className="p-4">
            <h2 className="mb-2 font-semibold text-foreground text-sm">
              About the role
            </h2>
            <div className="text-secondary-foreground text-sm leading-relaxed">
              <MdxContent>{listing.description}</MdxContent>
            </div>
          </CardContent>
        </Card>
      )}

      {bodySections.map((section) => (
        <Card className="gap-0 rounded-lg py-0" key={section.key}>
          <CardContent className="p-4">
            <h2 className="mb-3 font-semibold text-foreground text-sm">
              {section.label}
            </h2>
            <div className="text-secondary-foreground text-sm leading-relaxed">
              <MdxContent>{section.content}</MdxContent>
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}

export function ListingHero({ listing }: { listing: ApplicationListing }) {
  const {
    loginModalOpen,
    setLoginModalOpen,
    applyButtonLabel,
    isFavourited,
    isStartingApplication,
    isFavouriteButtonPending,
    handleStart,
    toggleFavourite,
    commitmentLabel,
    workModelLabel,
    locationText,
    posterLine,
    bodySections,
    userType,
    listingReturnTo,
  } = useListingHero(listing);

  return (
    <section>
      <LoginModal
        isOpen={loginModalOpen}
        onOpenChange={setLoginModalOpen}
        returnTo={listingReturnTo}
      />

      <div className="mb-2.5">
        <Link
          className="inline-flex items-center gap-1.5 text-muted-foreground text-xs transition-colors hover:text-foreground"
          href="/job-listings"
        >
          <ArrowLeft className="size-3.5" />
          Back to jobs
        </Link>
      </div>

      <div className="grid items-start gap-4 lg:grid-cols-[1fr_290px]">
        <main className="grid gap-3">
          <HeroHeaderCard
            applyButtonLabel={applyButtonLabel}
            commitmentLabel={commitmentLabel}
            isFavouriteButtonPending={isFavouriteButtonPending}
            isFavourited={isFavourited}
            isStartingApplication={isStartingApplication}
            listing={listing}
            locationText={locationText}
            onApply={handleStart}
            onToggleFavourite={toggleFavourite}
            posterLine={posterLine}
            userType={userType}
            workModelLabel={workModelLabel}
          />

          <ListingBody bodySections={bodySections} listing={listing} />
        </main>

        <ListingSidebar
          applyButtonLabel={applyButtonLabel}
          isFavouriteButtonPending={isFavouriteButtonPending}
          isFavourited={isFavourited}
          isStartingApplication={isStartingApplication}
          listing={listing}
          onApply={handleStart}
          onToggleFavourite={toggleFavourite}
        />
      </div>
    </section>
  );
}
